import { useCallback, useState } from 'react';

import { LOCALES } from '../../../helpers/enums';
import * as Validations from '../../../helpers/validations/notification-validations';
import { NOTIFICATION_TYPES } from '../../../helpers/constants';
import moment from 'moment';

const { EN, ES } = LOCALES;
const langs = [EN, ES];

export const useMessageNotificationForm = (groupId) => {
  const [errors, setErrors] = useState({
    type: '',
    title: { [EN]: '', [ES]: '' },
    message: { [EN]: '', [ES]: '' },
    i18nTargetUrl: { en: '', es: '' },
    sendAt: '',
  });

  const [values, setValues] = useState({
    type: groupId ? NOTIFICATION_TYPES.DANCERS.value : '',
    title: { [EN]: '', [ES]: '' },
    message: { [EN]: '', [ES]: '' },

    excludeInactive: true,
  });

  const handleTypeChange = useCallback(
    (e) => {
      const { name, value } = e.target;
      const urlType = '-';
      const i18nTargetUrl = { en: '', es: '' };
      setValues({ ...values, urlType, i18nTargetUrl, [name]: value });
    },
    [values, errors, setValues, setErrors]
  );

  const updateValues = useCallback(
    (field, name, value) => setValues({ ...values, [field]: { ...values[field], [name]: value } }),
    [setValues, values]
  );

  const updateErrors = useCallback(
    (field, name, value) => setErrors({ ...errors, [field]: { ...errors[field], [name]: value } }),
    [setErrors, errors]
  );

  const handleTitleChange = useCallback((e) => updateValues('title', e.target.name, e.target.value), [updateValues]);

  const handleMsgChange = useCallback((e) => updateValues('message', e.target.name, e.target.value), [updateValues]);

  const handleTitleBlur = useCallback(
    (e) => {
      const { name: lang, value } = e.target;
      const otherLang = [EN, ES].filter((i) => i !== lang)[0];
      const newValue = value.trim();
      const errorObj = {
        [lang]: Validations.validateTitle(newValue, values.title),
        [otherLang]: Validations.validateTitle(values.title[otherLang], { ...values.title, [lang]: newValue }),
      };
      setErrors({ ...errors, title: errorObj });
      updateValues('title', lang, newValue);
    },
    [setErrors, updateValues]
  );

  const handleMsgBlur = useCallback(
    (e) => {
      const { name: lang, value } = e.target;
      const newValue = value.trim();
      updateErrors(
        'message',
        lang,
        Validations.validateMessage({ ...values, message: { ...values.message, [lang]: newValue } }, lang, groupId)
      );
      updateValues('message', lang, newValue);
    },
    [updateErrors, updateValues, groupId]
  );

  const handleChange = ({ target: { name, value } }) => {
    if (name === 'urlType' && value !== 'all_dancers') {
      setErrors({ ...errors, i18nTargetUrl: { en: '', es: '' } });
    }
    setValues({ ...values, [name]: value });
  };

  const handleBlur = ({ target: { name, value } }) => {
    if (name.startsWith('targetUrl')) {
      const lan = name.split('targetUrl')[1].toLowerCase();
      setErrors({
        ...errors,
        i18nTargetUrl: {
          ...errors.i18nTargetUrl,
          [lan]: Validations.validateUrl(value),
        },
      });
    }
  };

  const handleChangeIncludeInactive = ({ target: { checked } }) => {
    setValues({ ...values, excludeInactive: !checked });
  };

  const validateForm = useCallback(() => {
    let isValid = true;
    const ERROR_OBJ = {
      type: Validations.validateAudience(values.type),
      title: {
        en: Validations.validateTitle(values.title.en, values.title),
        es: Validations.validateTitle(values.title.es, values.title),
      },
      message: {
        en: Validations.validateMessage(values, EN, groupId),
        es: Validations.validateMessage(values, ES, groupId),
      },
      i18nTargetUrl:
        values.urlType === 'url'
          ? {
              en: values.title.en ? Validations.validateUrl(values.targetUrlEn) : '',
              es: values.title.es ? Validations.validateUrl(values.targetUrlEs) : '',
            }
          : '',
    };

    if (ERROR_OBJ.type) isValid = false;

    ['title', 'message', 'i18nTargetUrl'].forEach((field) => {
      if (Object.values(ERROR_OBJ[field]).reduce((a, c) => a + c, '')) isValid = false;
    });

    setErrors(ERROR_OBJ);
    return isValid;
  }, [values, groupId]);

  return {
    values,
    errors,
    langs,
    handleMsgBlur,
    handleTitleBlur,
    handleMsgChange,
    handleTypeChange,
    handleTitleChange,
    handleChange,
    handleBlur,
    handleChangeIncludeInactive,
    validateForm,
  };
};
